import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  RefreshCw,
  Trash2,
  Clock,
  Users,
  Repeat,
  Loader2,
  FlaskConical,
} from "lucide-react";
import type { TestListItem } from "./types";
import { formatPstDateTime } from "./types";

interface TestHistoryPanelProps {
  testHistory: TestListItem[];
  selectedTestId: string | null;
  runningTestId: string | null;
  isLoadingHistory: boolean;
  onRefresh: () => void;
  onViewTest: (test: TestListItem) => void;
  onDeleteTest: (testId: string, e: React.MouseEvent) => void;
}

export const TestHistoryPanel = ({
  testHistory,
  selectedTestId,
  runningTestId,
  isLoadingHistory,
  onRefresh,
  onViewTest,
  onDeleteTest,
}: TestHistoryPanelProps) => {
  return (
    <Card className="flex min-h-0 flex-col overflow-hidden max-md:hidden border border-border/60 bg-white/80 shadow-sm">
      <CardHeader className="flex flex-row justify-between items-center space-y-0 border-b border-border/60">
        <div className="flex min-h-0 items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center">
            <FlaskConical className="h-4 w-4 text-muted-foreground" />
          </div>
          <CardTitle className="text-sm font-semibold">Test History</CardTitle>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onRefresh}
          title="Refresh"
          className="h-8 w-8 hover:bg-muted"
        >
          <RefreshCw
            className={`h-4 w-4 ${isLoadingHistory ? "animate-spin" : ""}`}
          />
        </Button>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto p-3">
        {isLoadingHistory ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mb-2" />
            <span className="text-xs">Loading tests...</span>
          </div>
        ) : testHistory.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <FlaskConical className="h-10 w-10 mb-3 opacity-30" />
            <span className="text-sm font-medium">No tests yet</span>
            <span className="text-xs mt-1">Start your first load test</span>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {testHistory.map((test) => {
              // Prefer meta.json's num_clients (known at launch); fall back to
              // the count of result files for pre-meta tests.
              const clients = test.num_clients ?? test.client_count ?? null;
              const isRunning = runningTestId === test.test_id;
              return (
                <div
                  key={test.test_id}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors hover:bg-muted group ${selectedTestId === test.test_id ? "border-primary bg-muted" : "border-border"}`}
                  onClick={() => onViewTest(test)}
                >
                  <div className="flex justify-between items-center mb-2">
                    <span
                      className="font-mono text-xs font-medium text-foreground cursor-pointer hover:text-primary hover:underline"
                      title="Click to copy test ID"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(test.test_id);
                        toast.success("Test ID copied to clipboard");
                      }}
                    >
                      {test.test_id.substring(0, 8)}
                    </span>
                    {isRunning && (
                      <Badge className="h-4 rounded-full border border-lime-600/30 bg-lime-600/15 px-1.5 text-[0.6rem] font-semibold text-lime-800 animate-pulse">
                        LIVE
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    <Badge
                      variant="secondary"
                      className="text-[0.65rem] px-2 py-0.5"
                    >
                      <Users className="h-3 w-3 mr-1" />
                      {clients ?? "—"}
                    </Badge>
                    <Badge
                      variant="secondary"
                      className="text-[0.65rem] px-2 py-0.5"
                    >
                      <Repeat className="h-3 w-3 mr-1" />
                      {test.turns ?? "—"} turns
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <Clock className="h-3 w-3" />
                      {formatPstDateTime(test.started_at ?? test.last_modified)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 hover:bg-muted hover:text-destructive transition-opacity"
                      onClick={(e) => onDeleteTest(test.test_id, e)}
                      title="Delete test"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
